import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Brand } from '@components/common/Brand';
import { Container } from '@components/common/Container';
import { footerNavigationLinks, legalLinks } from '@config/navigation';
import { siteConfig } from '@config/site';
export const Footer = () => {
    const { t } = useTranslation();
    return (_jsxs(FooterShell, { children: [_jsxs(FooterGrid, { children: [_jsxs(BrandBlock, { children: [_jsx(Link, { to: "/", "aria-label": `AITechCare — ${t('navigation.home')}`, children: _jsx(Brand, {}) }), _jsx("p", { children: t('footer.description') }), _jsx(BrandSignature, { children: "AI + Technology + Care" })] }), _jsxs(FooterColumn, { children: [_jsx("h2", { children: t('footer.navigation') }), footerNavigationLinks.map((link) => (_jsx(Link, { to: link.to, children: t(link.labelKey) }, link.to)))] }), _jsxs(FooterColumn, { children: [_jsx("h2", { children: t('footer.contact') }), _jsxs(AssistanceLink, { to: "/contact", children: [t('actions.requestAssistance'), _jsx(ArrowUpRight, { size: 16, "aria-hidden": "true" })] }), siteConfig.email && _jsx("a", { href: `mailto:${siteConfig.email}`, children: siteConfig.email }), siteConfig.phone && _jsx("a", { href: `tel:${siteConfig.phone}`, children: siteConfig.phone })] }), _jsxs(FooterColumn, { children: [_jsx("h2", { children: t('footer.legal') }), legalLinks.map((link) => (_jsx(Link, { to: link.to, children: t(link.labelKey) }, link.to)))] })] }), _jsxs(FooterBottom, { children: [_jsxs("span", { children: ["\u00A9 ", new Date().getFullYear(), " ", siteConfig.name] }), _jsxs("span", { children: [siteConfig.legalName, " \u00B7 NIF ", siteConfig.taxId] }), _jsx("span", { children: siteConfig.registeredAddress }), _jsx("span", { children: t('footer.signoff') })] })] }));
};
const FooterShell = styled.footer `
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  padding: clamp(3rem, 5vw, 5rem) 0 1.5rem;
  background: ${({ theme }) => theme.colors.surface};
`;
const FooterGrid = styled(Container) `
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2.5rem 1.5rem;
  @media (min-width: 1024px) {
    grid-template-columns: 1.4fr 0.65fr 1fr 1fr;
    gap: 3rem;
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;
const BrandBlock = styled.div `
  > a {
    display: inline-flex;
    min-height: 44px;
    align-items: center;
  }
  p {
    max-width: 280px;
    margin: 1rem 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.9375rem;
    line-height: 1.75;
  }
`;
const BrandSignature = styled.span `
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 0.6875rem;
  letter-spacing: 0.1em;
`;
const FooterColumn = styled.div `
  display: grid;
  align-content: start;
  h2 {
    margin: 0 0 0.75rem;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
  }
  a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.875rem;
    line-height: 1.5;
    overflow-wrap: anywhere;
  }
  a:hover {
    color: ${({ theme }) => theme.colors.navy900};
    text-decoration: underline;
    text-underline-offset: 4px;
  }
`;
const AssistanceLink = styled(Link) `
  gap: 0.5rem;
  svg {
    flex-shrink: 0;
  }
`;
const FooterBottom = styled(Container) `
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.5rem 2rem;
  margin-top: 3rem;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  padding-top: 1.5rem;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.75rem;
`;
